import { UserContext } from '@application/contexts';
import { Routes } from '@application/routes';
import LinkComponent from '@application/components/link-component/LinkComponent';
import { ReportAggregation, UserRole } from '@domain/graphql.types';
import { Download } from '@mui/icons-material';
import { alpha, Box, ButtonBase, Button, Divider, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

type Props = {
  onDownloadReport: (aggregation: ReportAggregation, months: number) => void;
  isDownloadReportDisabled: boolean;
  maxHistoricalMonths: number;
};

const monthOptions = [3, 6, 9, 12];

type OptionCardProps = {
  disabled?: boolean;
  isSelected: boolean;
  onClick: () => void;
  subtitle: string;
  title: string;
};

const OptionCard = ({ disabled = false, isSelected, onClick, subtitle, title }: OptionCardProps) => {
  const { palette } = useTheme();

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        textAlign: 'left',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          borderRadius: 2.5,
          backgroundColor: isSelected ? alpha(palette.primary.main, 0.08) : alpha(palette.text.primary, 0.035),
          color: disabled ? palette.text.disabled : palette.text.primary,
          paddingX: 1.5,
          paddingY: 1.25,
          transition: 'all 140ms ease',
        }}
      >
        <Typography fontSize={13} fontWeight={700}>
          {title}
        </Typography>
        <Typography color="text.secondary" fontSize={12}>
          {subtitle}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

const DownloadReportMenu = ({ onDownloadReport, isDownloadReportDisabled, maxHistoricalMonths }: Props) => {
  const [open, setOpen] = useState(false);
  const [aggregation, setAggregation] = useState<ReportAggregation>(ReportAggregation.Weekly);
  const [months, setMonths] = useState(3);

  const { t } = useTranslation();
  const { palette } = useTheme();
  const { hasSupportForAggregation, role, tenant } = useContext(UserContext);

  const availableMonths = useMemo(() => monthOptions.filter((m) => m <= maxHistoricalMonths), [maxHistoricalMonths]);
  const isWeeklyDisabled = !hasSupportForAggregation(ReportAggregation.Weekly);
  const isDailyDisabled = !hasSupportForAggregation(ReportAggregation.Daily);
  const showLinkToSubscription = role === UserRole.Owner;

  useEffect(() => {
    if (!isWeeklyDisabled) {
      setAggregation(ReportAggregation.Weekly);
      return;
    }

    if (!isDailyDisabled) {
      setAggregation(ReportAggregation.Daily);
    }
  }, [isDailyDisabled, isWeeklyDisabled]);

  const isConfirmDisabled = useMemo(() => {
    if (isDownloadReportDisabled) {
      return true;
    }

    if (aggregation === ReportAggregation.Daily) {
      return isDailyDisabled;
    }

    return isWeeklyDisabled;
  }, [aggregation, isDailyDisabled, isDownloadReportDisabled, isWeeklyDisabled]);

  const handleOpen = useCallback(() => {
    if (!isDownloadReportDisabled) {
      setOpen(true);
    }
  }, [isDownloadReportDisabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    onDownloadReport(aggregation, months);
    setOpen(false);
  }, [aggregation, months, onDownloadReport]);

  return (
    <>
      <Box>
        <Button
          disabled={isDownloadReportDisabled}
          onClick={handleOpen}
          startIcon={<Download />}
          sx={{ borderRadius: 2.5, minWidth: 148, px: 2 }}
          variant="contained"
        >
          {t('siteLookup.downloadReport')}
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 4,
            width: 480,
            maxWidth: 'calc(100vw - 32px)',
            boxShadow: '0 18px 60px rgb(20 24 40 / 18%)',
          },
        }}
      >
        <DialogTitle sx={{ paddingX: 3, paddingTop: 3, paddingBottom: 1.5 }}>
          <Stack gap={1}>
            <Typography fontSize={22} fontWeight={700}>
              {t('siteLookup.downloadDialog.title')}
            </Typography>
            <Box
              sx={{
                width: 'fit-content',
                borderRadius: 999,
                backgroundColor: alpha(palette.primary.main, 0.08),
                color: 'primary.main',
                paddingX: 1.5,
                paddingY: 0.5,
              }}
            >
              <Typography fontSize={13} fontWeight={600}>
                <Trans
                  i18nKey={'aggregationMenu.subscriptionInfo'}
                  t={t}
                  values={{ count: tenant?.remainingDownloads }}
                  components={{
                    strong: <strong />,
                    Upgrade: showLinkToSubscription ? (
                      <Link color="inherit" component={LinkComponent} href={Routes.TENANT_SUBSCRIPTION} sx={{ fontWeight: 'bold' }}>
                        {t('siteLookup.upgrade')}
                      </Link>
                    ) : (
                      <></>
                    ),
                  }}
                />
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ paddingX: 3, paddingBottom: 2, paddingTop: 0 }}>
          <Stack gap={3}>
            <Box sx={{ paddingY: 0.5 }}>
              <Typography fontSize={14} fontWeight={700} marginBottom={1.25}>
                {t('siteLookup.downloadDialog.aggregationLabel')}
              </Typography>
              <Box display="grid" gap={1.25} gridTemplateColumns="repeat(2, minmax(0, 1fr))">
                <OptionCard
                  disabled={isDailyDisabled}
                  isSelected={aggregation === ReportAggregation.Daily}
                  onClick={() => setAggregation(ReportAggregation.Daily)}
                  subtitle={t('siteLookup.downloadDialog.aggregationOption')}
                  title={t('enum.reportAggregation.DAILY')}
                />
                <OptionCard
                  disabled={isWeeklyDisabled}
                  isSelected={aggregation === ReportAggregation.Weekly}
                  onClick={() => setAggregation(ReportAggregation.Weekly)}
                  subtitle={t('siteLookup.downloadDialog.aggregationOption')}
                  title={t('enum.reportAggregation.WEEKLY')}
                />
              </Box>
            </Box>

            <Box sx={{ paddingY: 0.5 }}>
              <Typography fontSize={14} fontWeight={700} marginBottom={1.25}>
                {t('siteLookup.downloadDialog.periodLabel')}
              </Typography>
              <Box display="grid" gap={1.25} gridTemplateColumns={`repeat(${Math.min(availableMonths.length, 4)}, minmax(0, 1fr))`}>
                {availableMonths.map((option) => (
                  <OptionCard
                    key={option}
                    isSelected={months === option}
                    onClick={() => setMonths(option)}
                    subtitle={t('siteLookup.downloadDialog.monthsShort')}
                    title={String(option)}
                  />
                ))}
              </Box>
              {availableMonths.length === 1 && (
                <Typography color="text.secondary" fontSize={12} marginTop={1.25}>
                  <Trans
                    i18nKey={'siteLookup.downloadDialog.needMoreHistory'}
                    t={t}
                    components={{
                      ContactUs: (
                        <Link color="inherit" href="mailto:support@paren.app" sx={{ fontWeight: 600 }}>
                          {t('siteLookup.downloadDialog.contactUs')}
                        </Link>
                      ),
                    }}
                  />
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ paddingX: 3, paddingY: 2.25 }}>
          <Button color="inherit" onClick={handleClose}>
            {t('button.cancel')}
          </Button>
          <Button disabled={isConfirmDisabled} onClick={handleConfirm} sx={{ borderRadius: 2.5, px: 2 }} variant="contained">
            {t('siteLookup.downloadDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DownloadReportMenu;
